import {
    resolveLocalizedName,
    SchemaAppsSessionExpiredMessage,
    SchemaSlotLocationMessage,
    SchemaSlotTokenMessage,
    SchemaWidgetBackMessage,
    SchemaWidgetErrorInbound,
    SchemaWidgetNavigateMessage,
    SchemaWidgetNotifyMessage,
    SchemaWidgetOpenMessage,
    SchemaWidgetReadyMessage,
    SchemaWidgetResizeMessage,
    type SlotId,
    WIDGET_PROTOCOL_VERSION,
    type WidgetLocation,
} from '@luminovo/apps-types';
import { TokenContext } from '@luminovo/auth';
import { CircularProgress, linkOptions, useBackNavigation } from '@luminovo/design-system';
import { Box } from '@mui/material';
import {
    type CSSProperties,
    type ReactElement,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { useSlotAnalytics, widgetAttribution } from '../analytics/slotAnalytics';
import type { AddressableSlotAssignment } from '../api/fetchSlotAssignments';
import { useEffectHandlers } from '../effects/useEffectHandlers';
import { hasTransientActivation } from '../effects/userActivation';
import { useAppsSession } from '../hooks/useAppsSession';
import { useFrameHeight } from '../hooks/useFrameHeight';
import { EMBEDDED_WIDGET_KINDS } from '../hooks/useSlotEmbeds';
import { useWidgetVisibility } from '../hooks/useWidgetVisibility';
import {
    hasParentOrigin,
    reportWidgetSignal,
    signalFromWidget,
    type WidgetErrorSignal,
} from '../observability/reportWidgetSignal';

export type SlotContainerState = 'loading' | 'ready' | 'error';

/**
 * What the widget may do with the page's URL. A full-page widget owns the page
 * (`owns-page`). An embedded one, such as a container, sits in a page the user
 * opened for something else (`embedded`): it navigates only right after a user
 * click, never replaces the page's history entry, and cannot go back.
 */
export type SlotNavigation = 'owns-page' | 'embedded';

// Why a frame ended up unusable. Reported on `apps/widget_failed`, and the reason
// is the whole value of that event: "widgets fail 4% of the time" prompts nothing,
// while "3% of failures are ready_timeout on one app" names the bug.
type FrameFailureReason = 'session_unavailable' | 'ready_timeout' | 'load_error' | 'session_recovery_exhausted';

// Consecutive session re-mints this container will attempt before giving up on a
// frame. Consecutive, not lifetime: the counter resets every time the widget comes
// back ready, so a page left open all day recovers from as many genuine expiries as
// it needs to. What the cap stops is the pathological case — a host where the cookie
// can never be used in the frame, which otherwise cycles 401 page → expiry message →
// re-mint → reload → 401 page forever, hammering /apps/_session and showing the user
// a permanent spinner. Two, so one ordinary retry is still free.
const MAX_CONSECUTIVE_SESSION_RECOVERIES = 2;

export interface SlotContainerProps {
    assignment: AddressableSlotAssignment;
    // The apps host from the same assignments response as `assignment`. Never a
    // value built here: it is both the iframe's origin and the only origin this
    // container will accept a message from or post one to.
    appsOrigin: string;
    slotId: SlotId;
    /** Slug of the tenant the product page is showing, handed to the widget in `slot.token`. */
    tenant: string;
    params: Readonly<Record<string, unknown>>;
    /** Current product-router location, synchronized into the widget. */
    location: Readonly<WidgetLocation>;
    /** Locale used for host-owned display surfaces such as the iframe title. */
    locale?: string;
    className?: string;
    style?: CSSProperties;
    loadingFallback?: ReactNode;
    /**
     * Shown while the frame is in error. A function gets `retry`, which re-mints the
     * apps session and reloads the frame, so the fallback can offer a Retry button.
     * For a `container`, the fallback is shown alone, at its own height: a compact
     * row, not the empty 400 px box a frame starts at.
     */
    errorFallback?: ReactNode | ((retry: () => void) => ReactNode);
    readyTimeoutMs?: number;
    /** Defaults to `owns-page`. */
    navigation?: SlotNavigation;
    onStateChange?(state: SlotContainerState): void;
}

export function SlotContainer({
    assignment,
    appsOrigin,
    slotId,
    tenant,
    params,
    location,
    locale = 'en',
    className,
    style,
    loadingFallback = <CircularProgress />,
    errorFallback = null,
    readyTimeoutMs = 10_000,
    navigation = 'owns-page',
    onStateChange,
}: SlotContainerProps): ReactElement {
    const token = useContext(TokenContext);
    const effects = useEffectHandlers();
    const { navigateBack } = useBackNavigation();
    const appNameRef = useRef(assignment.app.name);
    appNameRef.current = assignment.app.name;
    const appsSession = useAppsSession(appsOrigin);
    // Bumped when the frame reports its session expired; part of the iframe key,
    // so a bump remounts that one frame and nothing else on the page.
    const [reloadNonce, setReloadNonce] = useState(0);
    const iframeKey = `${assignment.app.id}:${assignment.widget.id}:${assignment.bundle_url}:${reloadNonce}`;
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const readyWindowRef = useRef<Window | null>(null);
    const timeoutRef = useRef<number>();
    // `token` is both the send gate and part of the slot context. Widgets that bundle
    // existing LumiQuote clients initialize their auth module from this value.
    const slotContextRef = useRef({
        token,
        appId: assignment.app.id,
        slotId,
        tenant,
        params,
        workflowRunUrlTemplate: assignment.workflow_run_url_template,
        locale,
    });
    slotContextRef.current = {
        token,
        appId: assignment.app.id,
        slotId,
        tenant,
        params,
        workflowRunUrlTemplate: assignment.workflow_run_url_template,
        locale,
    };
    const locationRef = useRef(location);
    locationRef.current = location;
    // Read through a ref so the message listener does not need re-binding when
    // the query observer hands back a new function identity.
    const refreshAppsSessionRef = useRef(appsSession.refetch);
    refreshAppsSessionRef.current = appsSession.refetch;
    const mountedRef = useRef(true);
    const recoveryAttemptsRef = useRef(0);
    const [frameState, setFrameState] = useState<SlotContainerState>('loading');

    const track = useSlotAnalytics();
    const attribution = useMemo(() => widgetAttribution(assignment, slotId), [assignment, slotId]);
    // Analytics is read through a ref, never closed over. A host that passes an
    // unmemoized `track` would otherwise change sendSlotToken's identity on every
    // render, and the message listener is bound on that identity — so instrumentation
    // would start tearing down and re-adding the bridge the widget depends on.
    const analyticsRef = useRef({ track, attribution, iframeKey });
    analyticsRef.current = { track, attribution, iframeKey };
    // The outer box rather than the iframe: it is the element that keeps its size
    // while the frame is hidden during loading, so the observer measures the slot the
    // user is looking at rather than a frame that briefly has no box.
    const containerRef = useRef<HTMLDivElement>(null);
    // When the current load attempt started, for `ready_ms`.
    const loadStartedAtRef = useRef<number>();
    const failureReasonRef = useRef<FrameFailureReason>();
    // Both events are once-per-instance, and both can be reached more than once:
    // sendSlotToken re-posts on every params change, and the error state re-renders.
    const renderedInstanceRef = useRef<string>();
    const failedInstanceRef = useRef<string>();
    // Behind a ref for the same reason analytics is: `report` is called from
    // beginLoading, and the message listener is bound on identities built from it.
    const signalContext = {
        instance: iframeKey,
        appId: assignment.app.id,
        widgetId: assignment.widget.id,
        slotId,
        appsOrigin,
    };
    const signalContextRef = useRef(signalContext);
    signalContextRef.current = signalContext;
    const reportedSignalsRef = useRef({ instance: '', signals: new Set<WidgetErrorSignal>() });

    // Re-armed on mount, not only cleared on unmount, so a StrictMode remount does
    // not leave the flag stuck false for the rest of the component's life.
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    // No session, no widget document: the frame would only ever load the 401 page.
    // Reported as `error` rather than a permanent spinner — the page-level
    // decision to render nothing at all belongs to whoever owns the page.
    const sessionReady = appsSession.data?.status === 'ready';
    const sessionFailed = appsSession.isError || appsSession.data?.status === 'unavailable';
    const state: SlotContainerState = sessionFailed ? 'error' : sessionReady ? frameState : 'loading';
    // A widget that never sends `widget.resize` — every widget published before
    // this message existed — keeps the height it has always had.
    const frameHeight = useFrameHeight({
        kind: assignment.widget.kind,
        instanceKey: iframeKey,
        ready: state === 'ready',
    });
    const reportHeight = frameHeight.report;

    const clearReadyTimeout = useCallback(() => {
        if (timeoutRef.current !== undefined) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = undefined;
        }
    }, []);

    // At most one report per signal per frame instance. Every route in is repeatable
    // — a native `error` event can fire more than once, a re-render re-runs the
    // assignment checks — while the failure behind it happened once.
    const report = useCallback((signal: WidgetErrorSignal, sdkVersion?: string) => {
        const { instance, ...context } = signalContextRef.current;
        if (reportedSignalsRef.current.instance !== instance) {
            reportedSignalsRef.current = { instance, signals: new Set() };
        }
        if (reportedSignalsRef.current.signals.has(signal)) {
            return;
        }
        reportedSignalsRef.current.signals.add(signal);
        reportWidgetSignal(signal, { ...context, sdkVersion });
    }, []);

    // Every path that puts this container back into `loading` arms the same deadline,
    // so there is no way into the loading state without a way out of it. The
    // recovery path needs this as much as the first load does: it awaits a POST to
    // /apps/_session, and without a deadline of its own a hung request would sit on
    // the loading fallback until the browser gives up on the fetch, minutes later.
    const beginLoading = useCallback(() => {
        clearReadyTimeout();
        setFrameState('loading');
        loadStartedAtRef.current = performance.now();
        timeoutRef.current = window.setTimeout(() => {
            failureReasonRef.current = 'ready_timeout';
            setFrameState('error');
            report('widget_handshake_timeout');
        }, readyTimeoutMs);
    }, [clearReadyTimeout, readyTimeoutMs, report]);

    // Starts over from a failure, whichever it was: a refused or expired session, a
    // handshake that never came, a frame that did not load. The session is re-minted
    // first, so a reload does not fetch the 401 page again, then the frame is
    // recreated on a new key, which also reports a new failure if it fails again.
    const retry = useCallback(() => {
        recoveryAttemptsRef.current = 0;
        readyWindowRef.current = null;
        beginLoading();
        void refreshAppsSessionRef
            .current()
            .catch(() => {})
            .finally(() => {
                if (mountedRef.current) setReloadNonce((nonce) => nonce + 1);
            });
    }, [beginLoading]);

    const sendLocation = useCallback((): boolean => {
        const iframeWindow = iframeRef.current?.contentWindow;
        if (!slotContextRef.current.token || !iframeWindow || readyWindowRef.current !== iframeWindow) {
            return false;
        }
        const message = SchemaSlotLocationMessage.safeParse({
            protocol: WIDGET_PROTOCOL_VERSION,
            type: 'slot.location',
            ...locationRef.current,
        });
        // Never at the cost of slot.token, which is sent next and lets the frame render.
        if (!message.success) {
            report('widget_location_unsendable');
            return false;
        }
        iframeWindow.postMessage(message.data, appsOrigin);
        return true;
    }, [appsOrigin, report]);

    const sendSlotToken = useCallback((): boolean => {
        const iframeWindow = iframeRef.current?.contentWindow;
        if (!slotContextRef.current.token || !iframeWindow || readyWindowRef.current !== iframeWindow) {
            return false;
        }
        const message = SchemaSlotTokenMessage.parse({
            protocol: WIDGET_PROTOCOL_VERSION,
            type: 'slot.token',
            ...slotContextRef.current,
        });
        // Pinned to the apps origin, never '*': the message carries the product bearer
        // and business ids for whatever the user is looking at. The source-window
        // handshake also prevents a different frame on that origin receiving them.
        iframeWindow.postMessage(message, appsOrigin);
        clearReadyTimeout();
        // A healthy widget clears the recovery budget: the next expiry, however much
        // later, gets a full set of attempts again.
        recoveryAttemptsRef.current = 0;
        setFrameState('ready');

        // Once per frame instance, not per call: this runs again on every params
        // change, and a widget re-rendered with new route params has not loaded again.
        const { track: emit, attribution: dimensions, iframeKey: instance } = analyticsRef.current;
        if (renderedInstanceRef.current !== instance) {
            renderedInstanceRef.current = instance;
            emit('apps/widget_rendered', {
                ...dimensions,
                // Measured from when this frame instance became current, so on a cold
                // page it includes minting the apps session — deliberately, since
                // "how long until the widget is usable" is the number worth watching.
                ready_ms:
                    loadStartedAtRef.current === undefined
                        ? 0
                        : Math.round(performance.now() - loadStartedAtRef.current),
            });
        }
        return true;
    }, [appsOrigin, clearReadyTimeout]);

    useEffect(() => {
        readyWindowRef.current = null;
        clearReadyTimeout();
        // oxlint-disable-next-line react/set-state-in-effect -- SOU-4697: the extra render pass is kept deliberately; deriving this during render or resetting via `key` would change what the user sees
        setFrameState('loading');
        loadStartedAtRef.current = performance.now();
        failureReasonRef.current = undefined;
    }, [clearReadyTimeout, iframeKey]);

    useEffect(() => {
        onStateChange?.(state);
    }, [onStateChange, state]);

    // Attached natively rather than as an `onError` prop, because React does not wire
    // `error` for `iframe` — it does for `img`, and it does wire `load` for `iframe`,
    // which is why the prop looked like it worked. As a prop it was silently inert, so
    // a frame that failed to load sat on the spinner until the ready deadline.
    //
    // Note what this still cannot catch: a widget URL answering 404 or 500 loads the
    // error body INTO the frame and fires `load`, not `error`. From out here that is
    // indistinguishable from a successful load — the document is cross-origin — so
    // HTTP failures are still the ready deadline's job, and this covers the
    // network-level failures it was always meant to (refused connection, DNS, a
    // frame-src refusal).
    useEffect(() => {
        const iframe = iframeRef.current;
        if (iframe === null) {
            return;
        }
        const onFrameError = () => {
            clearReadyTimeout();
            readyWindowRef.current = null;
            failureReasonRef.current = 'load_error';
            setFrameState('error');
            report('widget_frame_load_error');
        };
        iframe.addEventListener('error', onFrameError);
        return () => iframe.removeEventListener('error', onFrameError);
        // `sessionReady` gates whether the element exists at all, so it decides
        // whether there is anything here to attach to.
    }, [clearReadyTimeout, iframeKey, report, sessionReady]);

    // One failure per frame instance. Every route into `error` names itself in
    // failureReasonRef first, except a failed session — which never touches
    // frameState at all, because `state` derives it.
    useEffect(() => {
        if (state !== 'error' || failedInstanceRef.current === iframeKey) {
            return;
        }
        failedInstanceRef.current = iframeKey;
        track('apps/widget_failed', {
            ...attribution,
            // The ready deadline is armed on every path into `loading`, so it is the
            // catch-all that would fire if some future route into `error` forgot to
            // name itself.
            reason: sessionFailed ? 'session_unavailable' : (failureReasonRef.current ?? 'ready_timeout'),
        });
    }, [attribution, iframeKey, sessionFailed, state, track]);

    // Two checks on our own assignments payload, neither of which shows up as itself:
    // a bundle URL with no parent origin reaches the user as a handshake that never
    // completes, and a missing run template sends the SDK to a fallback path whose
    // 404 reaches the author as "you declared no such workflow".
    useEffect(() => {
        if (!hasParentOrigin(assignment.bundle_url)) {
            report('widget_parent_origin_missing');
        }
        if (assignment.workflow_run_url_template === undefined) {
            report('widget_run_template_missing');
        }
    }, [assignment.bundle_url, assignment.workflow_run_url_template, report]);

    useWidgetVisibility({
        ref: containerRef,
        // A slot showing a spinner or an error fallback is on screen, but nobody has
        // seen the widget — so neither the view nor its dwell should count.
        enabled: state === 'ready',
        instanceKey: iframeKey,
        onViewed: () => track('apps/widget_viewed', attribution),
        onHidden: (visibleMs) => track('apps/widget_hidden', { ...attribution, visible_ms: visibleMs }),
    });

    useEffect(() => {
        sendSlotToken();
    }, [assignment.app.id, assignment.workflow_run_url_template, locale, params, sendSlotToken, slotId, tenant, token]);

    useEffect(() => {
        sendLocation();
    }, [location.hash, location.pathname, location.search, sendLocation]);

    useEffect(() => {
        // For the app's author, who sees nothing else: the request just does nothing.
        const warnRefused = (request: string, reason: string) => {
            const { appId } = signalContextRef.current;
            console.warn(`[apps] ${appNameRef.current} (${appId}): ${request} refused: ${reason}.`);
        };

        const onMessage = (event: MessageEvent) => {
            // Any page can postMessage into this window. The origin check against
            // the origin the server gave us — plus `event.source` identifying the
            // one frame this container owns — is what makes the rest trustworthy.
            if (event.origin !== appsOrigin) {
                return;
            }
            const iframeWindow = iframeRef.current?.contentWindow;
            if (!iframeWindow || event.source !== iframeWindow) {
                return;
            }

            if (SchemaWidgetReadyMessage.safeParse(event.data).success) {
                readyWindowRef.current = iframeWindow;
                // Location first, then context: by the time slot.token lets the widget
                // render its ready state, useLocation already reflects the host route.
                sendLocation();
                sendSlotToken();
                return;
            }

            const resize = SchemaWidgetResizeMessage.safeParse(event.data);
            if (resize.success) {
                // The widget is the only one who can measure this: an iframe has no
                // intrinsic height and this container cannot read the layout of a
                // cross-origin document.
                //
                // Nothing to validate beyond the schema's bounds — a height cannot name
                // a destination, so unlike `widget.navigate` there is no boundary here
                // to hold, only a page not to let a widget make absurdly tall.
                reportHeight(resize.data.height);
                return;
            }

            const navigateRequest = SchemaWidgetNavigateMessage.safeParse(event.data);
            if (navigateRequest.success) {
                // A widget cannot navigate itself: its sandbox omits allow-popups and
                // allow-top-navigation, so target="_blank"/"_top" are blocked and a plain
                // link would load the product INSIDE the widget frame. So it asks, and
                // the product routes.
                //
                // Safe to act on a customer-authored request because of what it cannot
                // express: the schema admits only an origin-relative path (no scheme, no
                // protocol-relative `//host`, no backslash), so this can only ever move
                // WITHIN the product — and the destination still faces whatever
                // permission predicate its route declares, exactly as if the user had
                // clicked a link in the product's own chrome.
                //
                // An embedded widget does not own the page, so it may not take the
                // user elsewhere unprompted, and may not overwrite the history entry
                // of the page it sits in.
                if (navigation === 'embedded') {
                    const refusal = navigateRequest.data.replace
                        ? 'a widget embedded in a page may not replace its history entry'
                        : !hasTransientActivation()
                          ? 'a widget embedded in a page may navigate only right after a user click'
                          : undefined;
                    if (refusal !== undefined) {
                        warnRefused(`widget.navigate to ${navigateRequest.data.path}`, refusal);
                        return;
                    }
                }
                // The only engagement signal this container can see. Everything else
                // a user does inside the frame is cross-origin and invisible from
                // here, so a widget that never links into the product reports none.
                const { track: emit, attribution: dimensions } = analyticsRef.current;
                emit('apps/widget_navigated', dimensions);
                // Stamped on the history entry so the destination can tell who sent the
                // user. A widget builds a product URL out of whatever it inferred, and a
                // page it got wrong is not the product's to answer for.
                effects.navigate(
                    { path: navigateRequest.data.path, replace: navigateRequest.data.replace },
                    { appId: dimensions.app },
                );
                return;
            }

            if (SchemaWidgetBackMessage.safeParse(event.data).success) {
                if (navigation === 'embedded') {
                    warnRefused('widget.back', 'a widget embedded in a page does not own its history');
                    return;
                }
                if (hasTransientActivation()) {
                    // Back within the product only; after a deep link the landing page stands in for that entry.
                    navigateBack({ fallback: linkOptions({ to: '/' }) });
                }
                return;
            }

            const open = SchemaWidgetOpenMessage.safeParse(event.data);
            if (open.success) {
                // Refused without a user gesture: the handler checks.
                effects.open({ url: open.data.url });
                return;
            }

            const notify = SchemaWidgetNotifyMessage.safeParse(event.data);
            if (notify.success) {
                const { iframeKey: frame, attribution: dimensions } = analyticsRef.current;
                // Throttled per frame instance, so a reloaded frame starts afresh.
                effects.notify(
                    { message: notify.data.message, level: notify.data.level },
                    { appId: dimensions.app, appName: appNameRef.current, frame },
                );
                return;
            }

            const widgetError = SchemaWidgetErrorInbound.safeParse(event.data);
            if (widgetError.success) {
                // Arrives from a customer-authored bundle, so the cap in `report` is
                // what keeps a render loop from spending Luminovo's Sentry quota. The
                // schema is the rest of it: a name from a bounded string and a version,
                // with no field an app could write prose into.
                report(signalFromWidget(widgetError.data.signal), widgetError.data.sdkVersion);
                return;
            }

            if (SchemaAppsSessionExpiredMessage.safeParse(event.data).success) {
                readyWindowRef.current = null;

                if (recoveryAttemptsRef.current >= MAX_CONSECUTIVE_SESSION_RECOVERIES) {
                    // Out of budget: the session keeps being accepted but the frame
                    // keeps rejecting it, so retrying is just a louder way of
                    // failing. Stop and say why — a silent retry loop is the one
                    // outcome that leaves nobody anything to debug.
                    clearReadyTimeout();
                    failureReasonRef.current = 'session_recovery_exhausted';
                    setFrameState('error');
                    console.error(
                        `[apps] ${assignment.app.id}:${assignment.widget.id} still has no usable session on ${appsOrigin} after ${MAX_CONSECUTIVE_SESSION_RECOVERIES} re-mints — giving up on this widget. The apps host is minting sessions this frame cannot use; check that this page is on the product host paired with ${appsOrigin}.`,
                    );
                    report('widget_session_unrecoverable');
                    return;
                }
                recoveryAttemptsRef.current += 1;

                beginLoading();
                // Re-mint first, then reload — a reload before the cookie is back
                // just fetches the 401 page again. Concurrent expiries across
                // frames share one in-flight POST via the query cache.
                void refreshAppsSessionRef
                    .current()
                    .then((result) => {
                        // Reload only on a session we know exists. A refetch
                        // RESOLVES for both outcomes, so treating resolution as
                        // success would reload the frame straight back into the 401
                        // page it just complained about. An unavailable result
                        // already renders as `error` through the derived state.
                        if (mountedRef.current && result.data?.status === 'ready') {
                            setReloadNonce((nonce) => nonce + 1);
                        }
                    })
                    // A rejected re-mint surfaces through the session query's own
                    // error state, which drops this frame. The deadline armed above
                    // covers the case where it neither resolves nor rejects.
                    .catch(() => {});
            }
        };

        window.addEventListener('message', onMessage);
        return () => {
            clearReadyTimeout();
            readyWindowRef.current = null;
            window.removeEventListener('message', onMessage);
        };
    }, [
        appsOrigin,
        clearReadyTimeout,
        effects,
        navigateBack,
        navigation,
        report,
        reportHeight,
        sendLocation,
        sendSlotToken,
    ]);

    const appliedHeight = frameHeight.applied;
    // An embedded widget sits in the page's own content, where an error is a row
    // of text, not a frame-sized hole. A full-page frame keeps its box.
    const errorAlone = state === 'error' && EMBEDDED_WIDGET_KINDS.has(assignment.widget.kind);

    return (
        <Box
            ref={containerRef}
            className={className}
            sx={{
                position: 'relative',
                width: '100%',
                ...(errorAlone ? {} : frameHeight.box),
            }}
            style={style}
            data-slot-state={state}
            data-slot-height={appliedHeight === undefined ? 'unsized' : appliedHeight}
        >
            {state === 'loading' && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {loadingFallback}
                </Box>
            )}
            {state === 'error' && (typeof errorFallback === 'function' ? errorFallback(retry) : errorFallback)}
            {/* Mounted only once the session exists, so the first request the frame
                makes is for the widget and not for the 401 page. */}
            {sessionReady && (
                <iframe
                    ref={iframeRef}
                    key={iframeKey}
                    src={assignment.bundle_url}
                    title={`${assignment.app.name}: ${resolveLocalizedName(assignment.widget.name, locale)}`}
                    // allow-same-origin is what lets the document send its
                    // `__Host-apps_sess` cookie and talk to its own APIs, so the
                    // widget cannot work without it.
                    //
                    // It does not weaken the PRODUCT boundary: this frame is
                    // cross-origin from the product either way, and the documented
                    // allow-scripts + allow-same-origin escape only applies to a
                    // frame that is same-origin with its embedder.
                    //
                    // What it does change is isolation BETWEEN a tenant's widgets.
                    // allow-scripts alone gave each frame an opaque origin of its
                    // own (which is why an earlier version of this file tested
                    // `event.origin === 'null'`); with allow-same-origin every
                    // widget on `<tenant>-apps.luminovo.com` shares one real origin,
                    // so they can reach each other's window and DOM, share
                    // localStorage/IndexedDB, and make same-origin calls to platform
                    // APIs carrying the tenant's session. That is the platform's
                    // accepted design, not something this component could opt out
                    // of — see the note atop lumi-mcp-server's src/serve/
                    // apps-storage.ts: "`:app` is a NAMESPACE, deliberately not an
                    // identity or a boundary — all of a tenant's apps share one
                    // origin and one session cookie, so per-app isolation can only
                    // ever be soft". The hard boundaries are tenant and user. The
                    // cookie is HttpOnly, so it stays out of document.cookie.
                    sandbox="allow-scripts allow-same-origin allow-downloads"
                    allow="clipboard-read; clipboard-write"
                    onLoad={() => {
                        // Real browsers deliver the widget's postMessage BEFORE
                        // dispatching the iframe's load event on the parent — the
                        // widget's <script> runs during document parsing while `load`
                        // waits for every subresource. If the handshake already
                        // succeeded (readyWindowRef points at this frame), don't
                        // clobber the ready state and arm a deadline that will fire
                        // while the widget is happily rendered.
                        const iframeWindow = iframeRef.current?.contentWindow;
                        if (iframeWindow && readyWindowRef.current === iframeWindow) {
                            return;
                        }
                        readyWindowRef.current = null;
                        beginLoading();
                    }}
                    // No onError here: React does not wire `error` for `iframe`, so the
                    // prop is silently inert. It is attached as a native listener in an
                    // effect above instead.
                    style={{
                        // An inline iframe reserves baseline descender space below itself. In a
                        // full-height slot that extra line-box space creates a second host scrollbar.
                        display: errorAlone ? 'none' : 'block',
                        width: '100%',
                        height: '100%',
                        minHeight: frameHeight.frameMinHeight,
                        border: 0,
                        visibility: state === 'ready' ? 'visible' : 'hidden',
                        // The host page is `color-scheme: only light`, and an iframe inherits it.
                        // Browsers derive the frame's `prefers-color-scheme` from this element's
                        // color-scheme, so without the reset a widget can never see the OS dark
                        // mode. `normal` hands the frame the user's preference again.
                        colorScheme: 'normal',
                    }}
                />
            )}
        </Box>
    );
}
